import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../../utils/joiConfig';
import {
  Input,
  Text,
  Select,
  Icon,
  Link,
  FormRow,
  Stack
} from '@efast_public/fjd-component-library';
import { Payment } from '../../api/govos.types';
import { XBezahldienstFormProps, PaymentSubFormProps } from './Payment.types';
import { usePaymentForm } from './usePaymentForm';
import { BezahldienstPolicy } from '../../api/models/Bezahldienst/BezahldienstDto';

const getDefaultValues = (
  data?: Payment,
  serviceId?: number
): XBezahldienstFormProps => ({
  serviceId: serviceId || 0,
  policy: data?.policy || 'OPTIONAL',
  clientId: data?.paymentService?.params?.clientId || '',
  clientSecret: data?.paymentService?.params?.clientSecret || '',
  originatorId: data?.paymentService?.params?.originatorId || '',
  endpointId: data?.paymentService?.params?.endpointId || '',
  descript: data?.paymentService?.params?.descript || ''
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<XBezahldienstFormProps>({
    serviceId: CustomJoi.number().required(),
    policy: CustomJoi.string().required(),
    clientId: CustomJoi.string().required(),
    clientSecret: CustomJoi.string().required(),
    originatorId: CustomJoi.string().required(),
    endpointId: CustomJoi.string().required(),
    descript: CustomJoi.string().required().max(27)
  });

export function XBezahldienstForm({
  paymentServices,
  payment,
  ...props
}: PaymentSubFormProps) {
  const { t } = useTranslation();
  const [showSecret, setShowSecret] = useState(false);
  const CustomJoi = createJoiWithTranslations(t);

  const { register, formState, handleSubmit, setValue, watch } =
    useForm<XBezahldienstFormProps>({
      defaultValues: getDefaultValues(
        payment,
        props.parentState.selectedService.serviceId
      ),
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  usePaymentForm({
    paymentServices,
    payment,
    handleSubmit,
    ...props
  });

  const config = watch();

  const policyOptions = [
    {
      value: 'mandatory',
      label: t('EditPaymentConfigModal.policyOptions.mandatory')
    },
    {
      value: 'optional',
      label: t('EditPaymentConfigModal.policyOptions.optional')
    }
  ];

  return (
    <Stack spacing="xs">
      {/* Policy Selection */}
      <FormRow
        label={t('XBezahldienstForm.fields.policy')}
        tooltip={t('XBezahldienstForm.fields.policyHint')}
        validationMessage={
          formState.errors.policy?.message
            ? t(formState.errors.policy.message)
            : undefined
        }
        variant={formState.errors.policy ? 'error' : undefined}
      >
        <Select
          value={config.policy}
          onChange={(value) =>
            setValue(
              'policy',
              (value.at(0)?.value || 'INACTIVE') as BezahldienstPolicy,
              {
                shouldValidate: true,
                shouldDirty: true
              }
            )
          }
          options={policyOptions}
          placeholder={t('select.placeholder')}
          style={{ width: '100%' }}
          defaultOptions={policyOptions.filter(
            (o) => o.value === config.policy
          )}
        />
      </FormRow>

      {/* Client-ID */}
      <FormRow
        label={t('XBezahldienstForm.fields.clientId')}
        tooltip={t('XBezahldienstForm.fields.clientIdHint')}
        validationMessage={
          formState.errors.clientId?.message
            ? t(formState.errors.clientId.message)
            : undefined
        }
        variant={formState.errors.clientId ? 'error' : undefined}
      >
        <Input
          {...register('clientId')}
          placeholder="01459823"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Client-Secret */}
      <FormRow
        label={t('XBezahldienstForm.fields.clientSecret')}
        tooltip={t('XBezahldienstForm.fields.clientSecretHint')}
        validationMessage={
          formState.errors.clientSecret?.message
            ? t(formState.errors.clientSecret.message)
            : undefined
        }
        variant={formState.errors.clientSecret ? 'error' : undefined}
      >
        <div style={{ position: 'relative' }}>
          <Input
            {...register('clientSecret')}
            type={showSecret ? 'text' : 'password'}
            placeholder="*********"
            style={{ width: '100%', paddingRight: '120px' }}
          />
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowSecret(!showSecret);
            }}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Icon name={showSecret ? 'data-vis-1' : 'data-vis-2'} size="sm" />
            <Text size="s">
              {showSecret
                ? t('XBezahldienstForm.actions.hidePassword')
                : t('XBezahldienstForm.actions.showPassword')}
            </Text>
          </Link>
        </div>
      </FormRow>

      {/* Originator-ID */}
      <FormRow
        label={t('XBezahldienstForm.fields.originatorId')}
        tooltip={t('XBezahldienstForm.fields.originatorIdHint')}
        validationMessage={
          formState.errors.originatorId?.message
            ? t(formState.errors.originatorId.message)
            : undefined
        }
        variant={formState.errors.originatorId ? 'error' : undefined}
      >
        <Input
          {...register('originatorId')}
          placeholder="Test"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Endpoint-ID */}
      <FormRow
        label={t('XBezahldienstForm.fields.endpointId')}
        tooltip={t('XBezahldienstForm.fields.endpointIdHint')}
        validationMessage={
          formState.errors.endpointId?.message
            ? t(formState.errors.endpointId.message)
            : undefined
        }
        variant={formState.errors.endpointId ? 'error' : undefined}
      >
        <Input
          {...register('endpointId')}
          placeholder="Test"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Buchungssatz */}
      <FormRow
        label={t('XBezahldienstForm.fields.descript')}
        tooltip={t('XBezahldienstForm.fields.descriptHint')}
        validationMessage={
          formState.errors.descript?.message
            ? t(formState.errors.descript.message)
            : undefined
        }
        variant={formState.errors.descript ? 'error' : undefined}
      >
        <Input
          {...register('descript')}
          placeholder="Verwendungszweck der Buchung"
          style={{ width: '100%' }}
        />
      </FormRow>
    </Stack>
  );
}
